// 📍 src/pages/UserGeography.jsx
//
// Where users are signing up and signing in from. Aggregates the
// `country`, `region`, `city`, and `signupCountry` fields written to
// `users/{uid}` by the `recordUserGeo` Cloud Function (called by the
// Flutter app at signup + login).
//
// Visual hierarchy:
//   1. KPI strip — total tracked users, top country, # countries reached.
//   2. Top countries bar chart (horizontal, top 10).
//   3. Country breakdown table — country, current users, new this month, % of total.
//   4. Top regions / cities tables (collapsible to save vertical space).
//   5. Recent signups feed — last 50 signups with their country.
//
// Data shape (from Cloud Function):
//   - users/{uid}.country, .region, .city, .countryCode  ← latest values
//   - users/{uid}.signupCountry, .signupRegion, .signupCity, .signupAt
//   - users/{uid}.lastLoginAt
//
// Until the Flutter app ships an AAB that calls recordUserGeo, these
// fields will be empty and the dashboard will show "no data". As soon
// as the new AAB lands and users start signing in, data flows in.

import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { PageHeader, StatCard, Card, Spinner } from '../components/UI';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';

const ACCENT = '#008751';
const GOLD   = '#D4AF37';

// Match Dashboard.jsx's chart tooltip styling
const CHART_TOOLTIP = {
  contentStyle: {
    background: '#1A2332',
    border: '1px solid rgba(0,135,81,0.2)',
    borderRadius: 10,
    fontSize: 12,
    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
  },
  itemStyle: { color: '#fff' },
  labelStyle: { color: 'rgba(255,255,255,0.5)', marginBottom: 4 },
};

// Tabular load cap. 1000 users is plenty for early stage; beyond that we'll
// move to a Cloud Function-backed aggregation. Marked here so it's easy to
// find when scale forces the migration.
const FETCH_USER_LIMIT = 1000;

export default function UserGeography() {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegions, setShowRegions] = useState(false);
  const [showCities, setShowCities] = useState(false);

  useEffect(() => { loadUsers(); }, []);

  async function loadUsers() {
    setLoading(true);
    try {
      // Pull the most-recently-active users first so the dashboard
      // reflects current state, not historical noise.
      const q = query(
        collection(db, 'users'),
        orderBy('lastLoginAt', 'desc'),
        limit(FETCH_USER_LIMIT),
      );
      const snap = await getDocs(q);
      setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('UserGeography load failed:', e);
    }
    setLoading(false);
  }

  // ── Aggregations ─────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const tracked = users.filter((u) => u.country); // only those with geo
    const byCountry = new Map(); // country → { count, newThisMonth, signups }
    const byRegion  = new Map();
    const byCity    = new Map();
    const monthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;

    for (const u of tracked) {
      const c = u.country || 'Unknown';
      if (!byCountry.has(c)) {
        byCountry.set(c, { country: c, code: u.countryCode, count: 0, newThisMonth: 0 });
      }
      byCountry.get(c).count++;
      const signupAt = u.signupAt?.toMillis ? u.signupAt.toMillis()
                     : (u.createdAt?.toMillis ? u.createdAt.toMillis() : 0);
      if (signupAt > monthAgo) byCountry.get(c).newThisMonth++;

      // Region key combines country + region for disambiguation (Lagos, NG vs Lagos, X)
      const r = u.region ? `${u.region}, ${u.country}` : null;
      if (r) {
        if (!byRegion.has(r)) byRegion.set(r, { region: r, count: 0 });
        byRegion.get(r).count++;
      }
      const ci = u.city ? `${u.city}, ${u.country}` : null;
      if (ci) {
        if (!byCity.has(ci)) byCity.set(ci, { city: ci, count: 0 });
        byCity.get(ci).count++;
      }
    }

    const countries = [...byCountry.values()].sort((a, b) => b.count - a.count);
    const regions   = [...byRegion.values()].sort((a, b) => b.count - a.count);
    const cities    = [...byCity.values()].sort((a, b) => b.count - a.count);

    return {
      tracked: tracked.length,
      untracked: users.length - tracked.length,
      countries,
      regions,
      cities,
      topCountry: countries[0],
    };
  }, [users]);

  const recentSignups = useMemo(() => {
    return users
      .filter((u) => u.signupAt && u.signupCountry)
      .sort((a, b) =>
        (b.signupAt?.toMillis?.() || 0) - (a.signupAt?.toMillis?.() || 0),
      )
      .slice(0, 50);
  }, [users]);

  if (loading) {
    return (
      <div style={{ padding: 40 }}>
        <Spinner />
      </div>
    );
  }

  // ── No-data state — pre-Flutter-AAB-ship ─────────────────────────────────
  if (stats.tracked === 0) {
    return (
      <div style={{ padding: 32 }}>
        <PageHeader
          title="User Geography"
          subtitle="Where your users are signing up and signing in from."
        />
        <Card>
          <div style={{ padding: 32, textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🌍</div>
            <h3 style={{ color: '#fff', marginBottom: 8 }}>Location data is populating…</h3>
            <p style={{ fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: '0 auto' }}>
              Geo is now stamped <b>server-side on every chat</b> (no app update needed).
              Each active user is geo-located on their next message, so this dashboard
              fills in as your users send messages. Check back shortly.
            </p>
            <p style={{ fontSize: 12, marginTop: 16, color: 'rgba(255,255,255,0.4)' }}>
              {users.length} user{users.length === 1 ? '' : 's'} loaded — geo back-fills
              as they chat (bounded to once / 24h each).
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 32 }}>
      <PageHeader
        title="User Geography"
        subtitle={`${stats.tracked} tracked users across ${stats.countries.length} ${stats.countries.length === 1 ? 'country' : 'countries'}`}
      />

      {/* KPI Strip */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16, marginBottom: 24,
      }}>
        <StatCard
          label="Tracked Users"
          value={stats.tracked.toLocaleString()}
          color={ACCENT}
        />
        <StatCard
          label="Top Country"
          value={stats.topCountry?.country || '—'}
          sub={stats.topCountry ? `${stats.topCountry.count} users` : ''}
          color={GOLD}
        />
        <StatCard
          label="Countries Reached"
          value={stats.countries.length}
          color="#3b82f6"
        />
        {stats.untracked > 0 && (
          <StatCard
            label="Untracked"
            value={stats.untracked.toLocaleString()}
            sub="Users without geo (older accounts)"
            color="rgba(255,255,255,0.4)"
          />
        )}
      </div>

      {/* Top Countries Bar Chart */}
      <Card style={{ marginBottom: 24 }}>
        <h3 style={{ color: '#fff', fontSize: 14, fontWeight: 700, marginBottom: 16,
          letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Top Countries
        </h3>
        <ResponsiveContainer width="100%" height={Math.max(260, 30 * Math.min(stats.countries.length, 10) + 60)}>
          <BarChart
            data={stats.countries.slice(0, 10)}
            layout="vertical"
            margin={{ top: 0, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis type="number" stroke="rgba(255,255,255,0.5)" fontSize={11} />
            <YAxis type="category" dataKey="country" stroke="rgba(255,255,255,0.7)"
              fontSize={11} width={140} />
            <Tooltip {...CHART_TOOLTIP} />
            <Bar dataKey="count" fill={ACCENT} radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Country Breakdown Table */}
      <Card style={{ marginBottom: 24 }}>
        <h3 style={{ color: '#fff', fontSize: 14, fontWeight: 700, marginBottom: 16,
          letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Country Breakdown
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <th style={thStyle}>Country</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Users</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>New (30d)</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>% of Total</th>
            </tr>
          </thead>
          <tbody>
            {stats.countries.map((c) => (
              <tr key={c.country} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={tdStyle}>
                  <span style={{ marginRight: 8 }}>{flagEmoji(c.code)}</span>
                  {c.country}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                  {c.count.toLocaleString()}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', color: c.newThisMonth > 0 ? ACCENT : 'rgba(255,255,255,0.4)' }}>
                  {c.newThisMonth > 0 ? '+' : ''}{c.newThisMonth}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  {((c.count / stats.tracked) * 100).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Top Regions (collapsible) */}
      <Card style={{ marginBottom: 24 }}>
        <button
          onClick={() => setShowRegions(!showRegions)}
          style={collapseHeaderBtn}
        >
          <span>Top Regions{stats.regions.length > 0 ? ` (${stats.regions.length})` : ''}</span>
          <span style={{ color: 'rgba(255,255,255,0.4)' }}>{showRegions ? '−' : '+'}</span>
        </button>
        {showRegions && (
          <div style={{ marginTop: 16, maxHeight: 360, overflowY: 'auto' }}>
            {stats.regions.slice(0, 50).map((r) => (
              <div key={r.region} style={listRowStyle}>
                <span>{r.region}</span>
                <span style={{ color: ACCENT, fontWeight: 600 }}>{r.count}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Top Cities (collapsible) */}
      <Card style={{ marginBottom: 24 }}>
        <button
          onClick={() => setShowCities(!showCities)}
          style={collapseHeaderBtn}
        >
          <span>Top Cities{stats.cities.length > 0 ? ` (${stats.cities.length})` : ''}</span>
          <span style={{ color: 'rgba(255,255,255,0.4)' }}>{showCities ? '−' : '+'}</span>
        </button>
        {showCities && (
          <div style={{ marginTop: 16, maxHeight: 360, overflowY: 'auto' }}>
            {stats.cities.slice(0, 50).map((c) => (
              <div key={c.city} style={listRowStyle}>
                <span>{c.city}</span>
                <span style={{ color: ACCENT, fontWeight: 600 }}>{c.count}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Recent Signups Feed */}
      <Card>
        <h3 style={{ color: '#fff', fontSize: 14, fontWeight: 700, marginBottom: 16,
          letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Recent Signups
        </h3>
        <div style={{ maxHeight: 480, overflowY: 'auto' }}>
          {recentSignups.length === 0 ? (
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, padding: 16 }}>
              No signups yet with location captured.
            </div>
          ) : recentSignups.map((u) => (
            <div key={u.id} style={listRowStyle}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ color: '#fff', fontSize: 13 }}>
                  {flagEmoji(u.signupCountryCode)} {u.signupCity || u.signupRegion || u.signupCountry}
                </span>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                  {u.email || '(no email)'} · {formatRelativeTime(u.signupAt)}
                </span>
              </div>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                {u.signupCountry}
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ── Style constants ─────────────────────────────────────────────────────────
const thStyle = {
  textAlign: 'left',
  padding: '12px 12px',
  fontSize: 11,
  color: 'rgba(255,255,255,0.5)',
  fontWeight: 600,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
};
const tdStyle = {
  padding: '14px 12px',
  color: 'rgba(255,255,255,0.85)',
};
const collapseHeaderBtn = {
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: 0,
  background: 'transparent',
  border: 'none',
  color: '#fff',
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const listRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 4px',
  fontSize: 13,
  color: 'rgba(255,255,255,0.85)',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
};

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Convert ISO country code (NG, US, GB) to a flag emoji. Falls back to "" */
function flagEmoji(countryCode) {
  if (!countryCode || countryCode.length !== 2) return '';
  // Each ISO letter → regional indicator emoji codepoint (U+1F1E6..)
  const offset = 0x1F1E6 - 0x41;
  const codePoints = [
    countryCode.toUpperCase().charCodeAt(0) + offset,
    countryCode.toUpperCase().charCodeAt(1) + offset,
  ];
  return String.fromCodePoint(...codePoints);
}

/** Human-readable relative time ("3h ago", "2d ago"). */
function formatRelativeTime(ts) {
  if (!ts) return '';
  const then = ts.toMillis ? ts.toMillis() : (ts.seconds ? ts.seconds * 1000 : new Date(ts).getTime());
  if (!then) return '';
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(then).toLocaleDateString();
}
